import { FatTransactionsPageAllocationTransactionsQuery } from '@/__generated__/graphql';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { useQuery } from '@apollo/client';
import { TenantType } from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';
import { NavigateOptions, useNavigate, useSearchParams } from 'react-router-dom';
import { ISelectValue } from '../basicComponents/select';
import Header from '../header';
import { ALL_ADVISORY_FIRM_LIST_QUERY } from '../investors-page/queries';
import { ITenant } from '../investors-page/types';
import { LoaderOnWholeScreen } from '../loaders/loader-on-whole-screen';
import { initialValuesFromTable } from '../table/constants';
import FilterPage from '../table/filterPage';
import { ISort, SORT_ORDER } from '../table/types';
import { transactionsMobileSort } from './constants';
import { ALLOCATION_TRANSACTIONS_QUERY, GET_OPERATION_WORKFLOW_TRANSITION_TASK_SUMMARIES_QUERY, INVESTMENTS_QUERY } from './queries';
import Table from './table/table';
import { IMobileSort, InvestmentsFilter } from './types';
import VisualFilteringBar, { IVisualFilteringBarData } from './visualFilteringBar';

const getFilteringBarParams = (filteringBarParams: string): string[] | null => {
  try {
    if (!filteringBarParams) return null;
    return JSON.parse(filteringBarParams);
  } catch (error) {
    return null;
  }
};

export const Transactions = () => {
  const { isMobile, isTablet } = useResponsive();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const q = searchParams.get('q') || '';
  const key = searchParams.get('key') || 'lastStatusDate';
  const asc = searchParams.get('asc') === 'true';
  const limit = searchParams.get('limit') || initialValuesFromTable.initialAmountRowsPerPage[0];
  const offset = searchParams.get('offset') || 0;
  const position = searchParams.get('position') || 0;
  const strategyFilter = searchParams.get('strategy') || '';
  const advisoryFirmQuickFilter = searchParams.get('advisoryFirmQuickFilter') || '';
  const filteringBarParams = getFilteringBarParams(searchParams.get('filteringBar'));

  const [visualFilteringBarData, setVisualFilteringBarData] = useState<IVisualFilteringBarData[]>([]);
  const [advisoryFirmQuickFilterOptions, setAdvisoryFirmQuickFilterOptions] = useState<ISelectValue[]>([]);
  const [strategyFilterOptions, setStrategyFilterOptions] = useState<ISelectValue[]>([]);
  const [allocations, setAllocations] = useState<FatTransactionsPageAllocationTransactionsQuery['allocationTransactions']['allocations']>([]);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [mobileSortItems, setMobileSortItems] = useState<IMobileSort[]>([]);
  const [mobileSort, setMobileSort] = useState('');
  const sortItem = mobileSortItems.find((item) => item.title === mobileSort);
  const mobileSortList = mobileSortItems.map((item) => item.title);
  const mobileSortValue = { key: sortItem?.key, asc: sortItem?.asc } as ISort;

  useEffect(() => {
    if (!user) return;

    if (user.tenant.type === TenantType.PLATFORM_MANAGER) {
      setMobileSortItems(transactionsMobileSort);
      setMobileSort(transactionsMobileSort[10].title);
      return;
    }
    if (user.tenant.type === TenantType.ADVISORY_FIRM) {
      const advisoryFirmMobileSort = transactionsMobileSort.slice(2);
      setMobileSortItems(advisoryFirmMobileSort);
      setMobileSort(transactionsMobileSort[10].title);
      return;
    }
  }, [user]);

  const { refetch: operationWorkflowTransitionTaskSummariesRefetch } = useQuery(GET_OPERATION_WORKFLOW_TRANSITION_TASK_SUMMARIES_QUERY, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      search: q.toLowerCase().trim(),
      tenantId: advisoryFirmQuickFilter,
      investmentId: strategyFilter,
      operationWorkflowTransitionTaskIds: filteringBarParams
    },
    onCompleted: (data) => {
      const { operationWorkflowTransitionTaskSummaries } = data.allocationTransactions;

      if (!filteringBarParams) {
        setVisualFilteringBarData(operationWorkflowTransitionTaskSummaries.map((item: any) => ({ ...item, enabled: false })));
      } else {
        const matchedFilterBarValues = operationWorkflowTransitionTaskSummaries.map((item: any) => {
          const matchedFilterItem = filteringBarParams.find((filterItem: string) => filterItem === item.id);

          return {
            ...item,
            enabled: Boolean(matchedFilterItem)
          };
        });
        setVisualFilteringBarData(matchedFilterBarValues);
      }
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const {
    data: allocationTransactionsData,
    refetch: allocationTransactionsRefetch,
    loading: allocationTransactionsLoading
  } = useQuery(ALLOCATION_TRANSACTIONS_QUERY, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      order: asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
      sort: key,
      search: q.toLowerCase().trim(),
      limit: Number(limit),
      offset: Number(offset),
      tenantId: advisoryFirmQuickFilter,
      investmentId: strategyFilter,
      operationWorkflowTransitionTaskIds: filteringBarParams
    },
    onCompleted: (data) => {
      const { allocations } = data.allocationTransactions;
      setAllocations(allocations);

      if (isFirstLoading) {
        setIsFirstLoading(false);
      }
      setTimeout(() => {
        makeScroll();
      }, 0);
    },
    onError: (error) => {
      setIsFirstLoading(false);
      console.error(error);
    }
  });

  const { loading: advisoryFirmListLoading } = useQuery<{ allAdvisoryFirmList: ITenant[] }>(ALL_ADVISORY_FIRM_LIST_QUERY, {
    skip: Boolean(user && user.tenant.type === TenantType.ADVISORY_FIRM),
    onCompleted: ({ allAdvisoryFirmList }) => {
      setAdvisoryFirmQuickFilterOptions([
        { label: 'All', value: '' },
        ...new Set(allAdvisoryFirmList.map((el) => ({ label: el.name, value: el.id })))
      ]);
    }
  });

  const { loading: recommendationInvestmentsListLoading } = useQuery<{ RecommendationInvestmentsList: InvestmentsFilter[] }>(INVESTMENTS_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setStrategyFilterOptions([
        { value: '', label: 'All' },
        ...data.RecommendationInvestmentsList.map((item) => ({ value: item.id, label: item.name }))
      ]);
    }
  });

  useEffect(() => {
    if (!isMobile) return;
    allocationTransactionsRefetch({
      order: sortItem?.asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
      sort: sortItem?.key
    });
  }, [mobileSort]);

  const openPage = (path: string, options?: NavigateOptions) => {
    saveScrollPostion();
    navigate(path, options);
  };

  const onChangeSearch = (searchValue: string) => {
    setSearchParams(
      (prev) => {
        if (!searchValue) {
          prev.delete('q');
          return prev;
        }
        prev.set('q', searchValue);
        prev.delete('limit');
        prev.delete('offset');
        return prev;
      },
      { replace: true }
    );
  };

  const onChangePaginationData = (limit: number, offset: number) => {
    setSearchParams(
      (prev) => {
        prev.set('limit', limit.toString());
        prev.set('offset', offset.toString());
        return prev;
      },
      { replace: true }
    );
  };

  const onChangeSort = (value: ISort) => {
    setSearchParams(
      (prev) => {
        prev.set('key', value.key);
        prev.set('asc', value.asc.toString());
        return prev;
      },
      { replace: true }
    );
  };

  const saveScrollPostion = () => {
    setSearchParams(
      (prev) => {
        prev.set('position', window.scrollY.toString());
        return prev;
      },
      { replace: true }
    );
  };

  const makeScroll = () => {
    if (!position) return;
    setTimeout(() => window.scrollTo({ top: Number(position), behavior: 'smooth' }), 0);
    setSearchParams(
      (prev) => {
        prev.delete('position');
        return prev;
      },
      { replace: true }
    );
  };

  const onChangeStrategyFilter = (filter: string) => {
    setSearchParams(
      (prev) => {
        filter ? prev.set('strategy', filter) : prev.delete('strategy');
        prev.delete('limit');
        prev.delete('offset');
        return prev;
      },
      { replace: true }
    );
  };

  const onChangeAdvisoryFirmQuickFilter = (advisoryFirmId: string) => {
    setSearchParams(
      (prev) => {
        advisoryFirmId ? prev.set('advisoryFirmQuickFilter', advisoryFirmId) : prev.delete('advisoryFirmQuickFilter');
        prev.delete('limit');
        prev.delete('offset');
        return prev;
      },
      { replace: true }
    );
  };

  const onChangeVisualFilteringBar = (filterBarItems: IVisualFilteringBarData[]) => {
    setVisualFilteringBarData(filterBarItems);
    const filteringBarIds = filterBarItems.filter((item) => item.enabled).map((item) => item.id);
    setSearchParams(
      (prev) => {
        prev.delete('limit');
        prev.delete('offset');
        if (!filteringBarIds.length) {
          prev.delete('filteringBar');
          return prev;
        }
        prev.set('filteringBar', JSON.stringify(filteringBarIds));
        return prev;
      },
      { replace: true }
    );
  };

  if (isFirstLoading || advisoryFirmListLoading || recommendationInvestmentsListLoading) {
    return <LoaderOnWholeScreen />;
  }

  return (
    <MainWrap>
      <MainWrap>
        <Header />
      </MainWrap>
      <PaddingWrap>
        {!isMobile && !isTablet && <VisualFilteringBar filterBar={visualFilteringBarData} onChangeFilterBar={onChangeVisualFilteringBar} />}
        <FilterPage
          search={{
            value: q,
            onChange: onChangeSearch,
            placeholder: 'Search'
          }}
          refetch={allocationTransactionsRefetch}
          resultsValue={allocationTransactionsData?.allocationTransactions?.total ?? 0}
          mobileSort={{
            value: mobileSort,
            selects: mobileSortList,
            onChange: setMobileSort
          }}
          {...(user &&
            user.tenant.type === TenantType.PLATFORM_MANAGER && {
              advisoryFirmQuickFilter: {
                title: 'Advisory Firm',
                value: advisoryFirmQuickFilter,
                defaultValue: 'All',
                options: advisoryFirmQuickFilterOptions,
                onChange: onChangeAdvisoryFirmQuickFilter
              }
            })}
          quickFilter={{
            title: 'Strategy',
            value: strategyFilter,
            defaultValue: 'All',
            options: strategyFilterOptions,
            onChange: onChangeStrategyFilter
          }}
          useNewFilters
        />
        <Table
          allocations={allocations}
          allocationTransactionsLoading={allocationTransactionsLoading}
          sort={isMobile ? mobileSortValue : { key, asc }}
          paginationData={{ limit: Number(limit), offset: Number(offset), total: allocationTransactionsData?.allocationTransactions?.total ?? 0 }}
          setSort={onChangeSort}
          openPage={openPage}
          allocationTransactionsRefetch={allocationTransactionsRefetch}
          setAllocations={setAllocations}
          onChangePaginationData={onChangePaginationData}
          operationWorkflowTransitionTaskSummariesRefetch={operationWorkflowTransitionTaskSummariesRefetch}
        />
      </PaddingWrap>
    </MainWrap>
  );
};
